import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import { sendMessageToAgent } from '../../services/agentChatService';
import WizardSubmissionService from '../../services/wizardSubmissionService';

const INITIAL_MESSAGE = {
  id: 'initial',
  role: 'assistant',
  content:
    'Hello! This chat is powered by our automation workflow. Ask your migration questions and I will forward them to the agent for a response.',
};

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const extractReplyText = (raw) => {
  if (!raw) return 'The automation returned an empty response.';

  const tryParseString = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try { return JSON.parse(trimmed); } catch { return null; }
    }
    return null;
  };

  const formatLabel = (key) => key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');

  const formatPrimitive = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const describeObjectForList = (value) => {
    if (!isPlainObject(value)) return formatPrimitive(value);
    return Object.entries(value)
      .map(([key, val]) => {
        const primitive = Array.isArray(val)
          ? val.map((item) => (isPlainObject(item) ? describeObjectForList(item) : formatPrimitive(item))).filter(Boolean).join(', ')
          : isPlainObject(val)
            ? describeObjectForList(val)
            : formatPrimitive(val);
        return primitive ? `${formatLabel(key)}: ${primitive}` : '';
      })
      .filter(Boolean)
      .join(' · ');
  };

  const formatArray = (values, indentLevel) => {
    const indent = '  '.repeat(indentLevel);
    return values
      .map((item) => {
        if (item === null || item === undefined) return '';
        if (Array.isArray(item)) {
          const nested = formatArray(item, indentLevel + 1);
          return nested ? `${indent}- ${nested}` : '';
        }
        if (isPlainObject(item)) {
          const summary = describeObjectForList(item);
          return summary ? `${indent}- ${summary}` : '';
        }
        const primitive = formatPrimitive(item);
        return primitive ? `${indent}- ${primitive}` : '';
      })
      .filter(Boolean)
      .join('\n');
  };

  const formatObject = (value, indentLevel = 0) => {
    const indent = '  '.repeat(indentLevel);
    const lines = Object.entries(value)
      .map(([key, val]) => {
        if (val === null || val === undefined) return '';
        if (Array.isArray(val)) {
          const formattedArray = formatArray(val, indentLevel + 1);
          return formattedArray ? `${indent}${formatLabel(key)}:\n${formattedArray}` : '';
        }
        if (isPlainObject(val)) {
          const nested = formatObject(val, indentLevel + 1);
          return nested ? `${indent}${formatLabel(key)}:\n${nested}` : '';
        }
        const primitive = formatPrimitive(val);
        return primitive ? `${indent}${formatLabel(key)}: ${primitive}` : '';
      })
      .filter(Boolean);
    return lines.join('\n');
  };

  const dig = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);
    if (value.response || value.reply || value.text || value.message || value.output) {
      return value.response || value.reply || value.text || value.message || value.output;
    }
    if (isPlainObject(value)) {
      const formatted = formatObject(value);
      if (formatted) return formatted;
    }
    return JSON.stringify(value, null, 2);
  };

  if (typeof raw === 'string') {
    const maybeJson = tryParseString(raw);
    if (maybeJson) return extractReplyText(maybeJson);
    return raw.trim();
  }
  return dig(raw);
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const units = ['bytes', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const LS_KEYS = {
  messages: 'global_chat_messages',
  sessionId: 'global_chat_session_id',
  unread: 'global_chat_unread',
  state: 'global_chat_state', // { open, minimized }
};

const GlobalChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const launcherRef = useRef(null);

  // Setup dynamic viewport units and keyboard offset for mobile
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    const setVisualViewportVars = () => {
      const vv = window.visualViewport;
      if (vv) {
        const vh = vv.height * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        const keyboardOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        document.documentElement.style.setProperty('--keyboard-offset', `${keyboardOffset}px`);
      }
    };
    // Update shared launcher offset so other floating elements (e.g., home CTA) stay clear
    const updateLauncherOffset = () => {
      try {
        let offset = 80; // default
        const gap = 16; // spacing above control
        // Prefer actual button size if present
        const el = launcherRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const btnSize = Math.max(rect.width, rect.height) || 64;
          offset = Math.round(btnSize + gap);
        } else {
          // Fallbacks based on state
          if (minimized) {
            // Docked bar height approx 56px
            offset = 56 + gap;
          } else {
            // Floating button
            offset = 64 + gap;
          }
        }
        document.documentElement.style.setProperty('--chat-launcher-offset', `${offset}px`);
      } catch { }
    };
    setVH();
    setVisualViewportVars();
    updateLauncherOffset();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    window.addEventListener('resize', updateLauncherOffset);
    window.addEventListener('orientationchange', updateLauncherOffset);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVisualViewportVars);
      window.visualViewport.addEventListener('scroll', setVisualViewportVars);
    }
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      window.removeEventListener('resize', updateLauncherOffset);
      window.removeEventListener('orientationchange', updateLauncherOffset);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setVisualViewportVars);
        window.visualViewport.removeEventListener('scroll', setVisualViewportVars);
      }
    };
  }, []);

  // Update offset when widget open/minimized state changes
  useEffect(() => {
    try {
      // Give DOM a tick to render new size
      const t = setTimeout(() => {
        const vv = window.visualViewport;
        if (vv) {
          const keyboardOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          document.documentElement.style.setProperty('--keyboard-offset', `${keyboardOffset}px`);
        }
        // Update the offset to reflect current control size
        const gap = 16;
        if (!open && !minimized && launcherRef.current) {
          const rect = launcherRef.current.getBoundingClientRect();
          const btnSize = Math.max(rect.width, rect.height) || 64;
          document.documentElement.style.setProperty('--chat-launcher-offset', `${Math.round(btnSize + gap)}px`);
        } else if (minimized) {
          document.documentElement.style.setProperty('--chat-launcher-offset', `${56 + gap}px`);
        } else {
          // Open panel: keep a reasonable bottom spacing for other CTAs
          document.documentElement.style.setProperty('--chat-launcher-offset', `${96}px`);
        }
      }, 0);
      return () => clearTimeout(t);
    } catch { }
  }, [open, minimized]);

  // Restore persisted state on mount
  useEffect(() => {
    try {
      const rawMessages = localStorage.getItem(LS_KEYS.messages);
      if (rawMessages) {
        const parsed = JSON.parse(rawMessages);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch { }
    try {
      const rawSession = localStorage.getItem(LS_KEYS.sessionId);
      if (rawSession) setSessionId(rawSession);
    } catch { }
    try {
      const rawUnread = localStorage.getItem(LS_KEYS.unread);
      const n = Number(rawUnread);
      if (Number.isFinite(n) && n > 0) setUnread(Math.min(99, n));
    } catch { }
    try {
      const rawState = localStorage.getItem(LS_KEYS.state);
      if (rawState) {
        const st = JSON.parse(rawState);
        if (typeof st?.open === 'boolean') setOpen(st.open);
        if (typeof st?.minimized === 'boolean') setMinimized(st.minimized);
      }
    } catch { }
  }, []);

  // Persist messages and session
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEYS.messages, JSON.stringify(messages));
    } catch { }
  }, [messages]);
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem(LS_KEYS.sessionId, sessionId);
    } catch { }
  }, [sessionId]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEYS.unread, String(unread));
    } catch { }
  }, [unread]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEYS.state, JSON.stringify({ open, minimized }));
    } catch { }
  }, [open, minimized]);

  // Auto scroll and manage unread when panel opens
  useEffect(() => {
    if ((open || minimized) && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    if (open) setUnread(0);
  }, [messages, open, minimized]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
    if (!open && msg.role === 'assistant') {
      setUnread((c) => Math.min(99, c + 1));
    }
  }, [open]);

  const ensureSession = () => {
    let eff = sessionId;
    if (!eff) {
      eff = createSessionId();
      setSessionId(eff);
    }
    return eff;
  };

  const forwardToWorkflow = async (messagePayload, metadata = {}) => {
    const effectiveSessionId = ensureSession();
    const { reply, suggestions, sessionId: responseSessionId } = await sendMessageToAgent({
      message: messagePayload,
      sessionId: effectiveSessionId,
      extraParams: metadata,
    });
    if (responseSessionId && responseSessionId !== effectiveSessionId) {
      setSessionId(responseSessionId);
    }
    const content = extractReplyText(reply);
    addMessage({
      id: `a-${Date.now()}`,
      role: 'assistant',
      content: content || 'The automation did not provide any content.',
      suggestions: Array.isArray(suggestions) ? suggestions : undefined,
    });
  };

  const handleSuggestionClick = async (suggestion) => {
    const value = typeof suggestion === 'string' ? suggestion : suggestion.value || suggestion.label;
    if (value) await handleUserInput(value);
  };

  const handleUserInput = async (text) => {
    if (!text || !text.trim()) return;
    const trimmed = text.trim();
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    addMessage(userMsg);
    setIsThinking(true);
    try {
      await forwardToWorkflow(trimmed);
    } catch (err) {
      const errorText = err?.message || 'Something went wrong while contacting the assistant.';
      addMessage({ id: `e-${Date.now()}`, role: 'assistant', content: `⚠️ ${errorText}` });
    } finally {
      setIsThinking(false);
    }
  };

  const handleFileUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    addMessage({ id: `upload-${Date.now()}`, role: 'assistant', content: `⏳ Reading ${file.name} (${formatFileSize(file.size)})…` });
    setIsThinking(true);
    try {
      // Validate and extract
      const { validateCVFile, extractCVText } = await import('../../services/cvParser');
      validateCVFile(file, 10);
      const cvText = await extractCVText(file);
      if (!cvText || cvText.trim().length < 50) {
        throw new Error('Could not extract enough text from the document. Please ensure the file has readable content.');
      }
      const cleanText = cvText.trim();
      const maxChars = 6000;
      const truncated = cleanText.length > maxChars ? `${cleanText.slice(0, maxChars)}\n\n[Text truncated after ${maxChars} characters]` : cleanText;
      addMessage({ id: `summary-${Date.now()}`, role: 'assistant', content: `📄 Parsed ${file.name}. Sending the extracted text to our automation...\n\nPreview:\n${truncated.slice(0, 500)}${truncated.length > 500 ? '…' : ''}` });
      const uploadInfo = await WizardSubmissionService.uploadCV(file, 'unknown');
      const cvPublicUrl = uploadInfo?.publicUrl || '';
      const cvPath = uploadInfo?.path || '';
      const cvSummary = truncated.slice(0, 500);
      const payload = `I am uploading my CV (${file.name}, ${formatFileSize(file.size)}).\nHere is the extracted text:\n\n${truncated}`;
      await forwardToWorkflow(payload, {
        filename: file.name,
        filesize: file.size.toString(),
        filetype: file.type || 'application/octet-stream',
        cv_url: cvPublicUrl,
        cv_public_url: cvPublicUrl,
        cv_storage_path: cvPath,
        cv_filename: uploadInfo?.fileName || file.name,
        cv_filesize: (uploadInfo?.fileSize ?? file.size).toString(),
        cv_summary: cvSummary,
      });
    } catch (err) {
      const errorText = err?.message || 'Failed to process the document. Please try again.';
      addMessage({ id: `error-${Date.now()}`, role: 'assistant', content: `⚠️ ${errorText}` });
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && !minimized && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-50 right-5 sm:right-6 w-16 h-16 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          aria-label="Open chat"
          ref={launcherRef}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-[11px] leading-5 text-white text-center">
                {unread}
              </span>
            )}
          </div>
        </button>
      )}

      {/* Minimized pill/docked bar */}
      {minimized && !open && (
        <div
          className="fixed z-50 bottom-0 left-0 right-0 sm:left-auto sm:right-6 sm:bottom-6"
        >
          <div className="mx-3 sm:mx-0 bg-white border border-gray-200 shadow-lg rounded-t-xl sm:rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <img src="/img/logo.png" alt="Ravana" className="w-6 h-6 rounded" />
                <span className="text-sm font-medium text-gray-800">Flight Plan™ Assistant</span>
                {unread > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-xs">{unread}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="px-2 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => { setMinimized(false); setOpen(false); }}
                  className="px-2 py-1 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} aria-hidden="true" />

          {/* Panel: full-screen on mobile, anchored card on larger screens */}
          <div
            className="absolute bottom-0 right-0 left-0 sm:left-auto sm:bottom-6 sm:right-6"
            style={{
              // On mobile, full height. On desktop, 70dvh or 70 * --vh
              height: 'auto',
            }}
          >
            <div
              className="bg-white shadow-xl rounded-t-2xl sm:rounded-2xl border border-gray-200 flex flex-col overflow-hidden"
              style={{
                width: '100%',
                maxWidth: '420px',
                // Height responsive: full on small screens; on sm+ use 70% viewport
                height: 'calc(100dvh - 64px)',
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <img src="/img/logo.png" alt="Ravana" className="w-8 h-8 rounded" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Flight Plan™ Assistant</h3>
                    <p className="text-xs text-gray-500">AI-Powered Migration Support</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setMinimized(true); setOpen(false); }}
                    className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center"
                    aria-label="Minimize chat"
                  >
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12h16" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setMinimized(false); }}
                    className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center"
                    aria-label="Close chat"
                  >
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 min-h-0 flex flex-col">
                <div
                  ref={scrollRef}
                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-5 py-3"
                  style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth', overscrollBehavior: 'contain' }}
                >
                  <div className="space-y-3">
                    {messages.map((m) => (
                      <ChatBubble key={m.id} message={m} onSuggestionClick={handleSuggestionClick} />
                    ))}
                    {isThinking && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="animate-pulse">The automation is thinking…</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Input */}
                <div className="flex-shrink-0 bg-white border-t border-gray-200 pb-safe kb-safe pt-2">
                  <div className="px-3 sm:px-4">
                    <ChatInput onSend={handleUserInput} isThinking={isThinking} onFileUpload={handleFileUpload} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalChatWidget;
